import { z } from 'zod';

const API_URL = import.meta.env.VITE_API_URL ?? '/api';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public issues?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  schema?: z.ZodTypeAny;
}

async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { schema, headers, ...init } = opts;

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
  });

  const ct = res.headers.get('content-type') ?? '';
  const isJson = ct.includes('application/json');
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      typeof body === 'object' && body && 'message' in body
        ? String(body.message)
        : `HTTP ${res.status}`;
    throw new ApiError(
      message,
      res.status,
      typeof body === 'object' && body && 'code' in body ? String(body.code) : undefined,
      typeof body === 'object' && body && 'issues' in body ? body.issues : undefined,
    );
  }

  // Defesa em profundidade: se a API base estiver mal configurada, o navegador
  // pode acabar batendo no SPA hospedado e receber HTML 200 com text/html.
  // Sem isso, o auth.refresh() aceitaria a string HTML como "user".
  if (!isJson) {
    console.error('API retornou non-JSON', { path, status: res.status, ct });
    throw new ApiError(
      'API indisponível ou mal configurada (resposta não-JSON)',
      502,
      'NON_JSON_RESPONSE',
    );
  }

  if (schema) {
    const parsed = schema.safeParse(body);
    if (!parsed.success) {
      console.error('API schema mismatch', parsed.error);
      throw new ApiError('Resposta inesperada do servidor', 500);
    }
    return parsed.data;
  }

  return body as T;
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T = unknown>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
};

export { ApiError };
